export class PersistentTaskQueue {
  constructor(dbName = 'TaskQueueDB', storeName = 'tasks') {
    this.dbName = dbName;
    this.storeName = storeName;
    this.db = null;
    this.isRunning = false;
    this.maxRetries = 5;
    this.retryBaseDelay = 1000;
    this.eventTarget = new EventTarget(); // 用于触发自定义事件
  }

  // 注册事件监听
  on(eventName, callback) {
    this.eventTarget.addEventListener(eventName, (event) => callback(event.detail));
  }

  // 触发事件
  dispatch(eventName, detail) {
    const event = new CustomEvent(eventName, { detail });
    this.eventTarget.dispatchEvent(event);
  }

  // 初始化 IndexedDB
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve();
      };

      request.onerror = (event) => {
        reject(new Error('Failed to open IndexedDB: ' + event.target.error));
      };
    });
  }

  // 生成唯一任务 ID
  generateTaskId() {
    return Date.now() + '-' + Math.random().toString(36).substring(2, 9);
  }

  // 添加任务
  async addTask(taskFn, params = {}, retries = 0) {
    if (!this.db) await this.initDB();

    const task = {
      id: this.generateTaskId(),
      taskFn: taskFn.toString(),
      params,
      status: 'pending',
      retries: retries,
      createdAt: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.add(task);

      request.onsuccess = async () => {
        resolve(task.id);
        this.dispatch('taskAdded', { task }); // 触发任务添加事件
        this.getAllTasks().then((tasks) => {
            this.dispatch('queueChanged', tasks); // 触发队列变化事件
        });
        this.processQueue();
      };
      request.onerror = () => reject(new Error('Failed to add task'));
    });
  }

  // 删除任务
  async deleteTask(taskId) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(taskId);

      request.onsuccess = async () => {
        resolve();
        this.dispatch('taskDeleted', { taskId }); // 触发任务删除事件
        this.getAllTasks().then((tasks) => {
            this.dispatch('queueChanged', tasks); // 触发队列变化事件
        });
      };
      request.onerror = () => reject(new Error('Failed to delete task'));
    });
  }

  // 获取任务
  async getTask(taskId) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(taskId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error('Failed to get task'));
    });
  }

  // 更新任务
  async updateTask(task) {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(task);

      request.onsuccess = () => {
        resolve();
        this.dispatch('taskUpdated', { task }); // 触发任务更新事件
        this.getAllTasks().then((tasks) => {
            this.dispatch('queueChanged', tasks); // 触发队列变化事件
        });
      };
      request.onerror = () => reject(new Error('Failed to update task'));
    });
  }

  // 获取所有任务
  async getAllTasks() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error('Failed to get all tasks'));
    });
  }
  async getPendingTasks() {
    if (!this.db) await this.initDB();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const pendingTasks = request.result.filter((task) => task.status === 'pending');
        resolve(pendingTasks);
      };
      request.onerror = () => reject(new Error('Failed to get all tasks'));
    });
  }

  async reset() {
    this.pause();
    const tasks = await this.getAllTasks();
    for(const task of tasks) {
        await this.deleteTask(task.id);
    }
  }

  // 暂停任务队列
  pause() {
    this.isRunning = false;
    this.dispatch('queuePaused', {}); // 触发队列暂停事件
  }

  // 恢复任务队列
  resume() {
    this.isRunning = true;
    this.dispatch('queueResumed', {}); // 触发队列恢复事件
    this.processQueue();
  }

  // 关闭任务队列
  async close() {
    this.isRunning = false;
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.dispatch('queueClosed', {}); // 触发队列关闭事件
  }

  // 计算重试延迟（指数退避）
  getRetryDelay(retryCount) {
    // return this.retryBaseDelay * Math.pow(2, retryCount);

  }

  // 处理任务队列
  async processQueue() {
    if (!this.isRunning) return;

    // const tasks = await this.getAllTasks();
    // const pendingTasks = tasks.filter((task) => task.status === 'pending');
    const pendingTasks = await this.getPendingTasks();

    if (!pendingTasks || pendingTasks.length === 0) return;

    const task = pendingTasks[0];
    await this.executeTask(task);
  }
  
  // 执行单个任务
  async executeTask(task) {
    if (!this.isRunning) return;

    task.status = 'running';
    await this.updateTask(task);
    let shouldExecuteNextTask = true;

    try {
      const taskFn = eval(`(${task.taskFn})`);
      await taskFn(task.params);

      task.status = 'completed';
      // await this.updateTask(task);
      await this.deleteTask(task.id);
    } catch (error) {
      task.retries = (task.retries || 0) + 1;

      if (task.retries < this.maxRetries) {
        shouldExecuteNextTask = false;
        // 移除当前任务
        await this.deleteTask(task.id);
        // 等待重试延迟后重新添加到队列尾部
        setTimeout(async () => {
          const newTask = {
            ...task,
            id: this.generateTaskId(), // 生成新 ID 确保唯一
            status: 'pending',
            createdAt: Date.now(),
          };
          await this.addTask(eval(`(${newTask.taskFn})`), newTask.params, task.retries);
        }, this.getRetryDelay(task.retries));
      } else {
        task.status = 'failed';
        // await this.updateTask(task);
        await this.deleteTask(task.id);
      }
    }

    if (shouldExecuteNextTask) {
        // 继续处理队列中的下一个任务
        this.processQueue();
    }
  }

  // 启动任务队列
  start() {
    this.isRunning = true;
    this.dispatch('queueStarted', {}); // 触发队列启动事件
    this.processQueue();
  }
}
/*
// 使用示例（包括 UI 更新）
async function example() {
  const queue = new TaskQueue();

  // 注册事件监听器
  queue.on('taskAdded', ({ task }) => {
    console.log(`Task added: ${task.id}, status: ${task.status}`);
    updateUI(); // 更新 UI
  });

  queue.on('taskDeleted', ({ taskId }) => {
    console.log(`Task deleted: ${taskId}`);
    updateUI(); // 更新 UI
  });

  queue.on('taskUpdated', ({ task }) => {
    console.log(`Task updated: ${task.id}, status: ${task.status}, retries: ${task.retries}`);
    updateUI(); // 更新 UI
  });

  queue.on('queueChanged', (tasks) => {
    console.log(`Queue changed, current tasks:`, tasks);
    updateUI(); // 更新 UI
  });

  queue.on('queueStarted', () => console.log('Queue started'));
  queue.on('queuePaused', () => console.log('Queue paused'));
  queue.on('queueResumed', () => console.log('Queue resumed'));
  queue.on('queueClosed', () => console.log('Queue closed'));

  async function updateUI() {
      
    const tasks = await queue.getPendingTasks();
      for (const task of tasks) {
    console.log(`Task ${task.id}: ${task.status} (Retries: ${task.retries || 0})`);}
  }
  // 示例 UI 更新函数
  async function updateUI() {
    const tasks = await queue.getAllTasks();
    const uiElement = document.getElementById('task-list');
    if (uiElement) {
      uiElement.innerHTML = tasks
        .map(
          (task) =>
            `<li>Task ${task.id}: ${task.status} (Retries: ${task.retries || 0})</li>`
        )
        .join('');
    }
  }

  // 示例任务函数
  const sampleTask = async (params) => {
    console.log('Processing task with params:', params);
    if (Math.random() < 0.5) throw new Error('Random task failure');
    console.log('Task completed successfully');
  };

  // 添加任务
  await queue.addTask(sampleTask, { data: 'Task 1' });
  await queue.addTask(sampleTask, { data: 'Task 2' });

  // 启动队列
  queue.start();

  // 示例操作
  setTimeout(() => queue.pause(), 5000);
  setTimeout(() => queue.resume(), 10000);
  setTimeout(() => queue.deleteTask((await queue.getAllTasks())[0].id), 12000);
  setTimeout(() => queue.close(), 15000);
}
// 运行示例
example();




  var queue = mailService.sendQueue;
  // 注册事件监听器
  queue.on('taskAdded', ({ task }) => {
    console.log(`Task added: ${task.id}, status: ${task.status}`);
    updateUI(); // 更新 UI
  });

  queue.on('taskDeleted', ({ taskId }) => {
    console.log(`Task deleted: ${taskId}`);
    updateUI(); // 更新 UI
  });

  queue.on('taskUpdated', ({ task }) => {
    console.log(`Task updated: ${task.id}, status: ${task.status}, retries: ${task.retries}`);
    updateUI(); // 更新 UI
  });

  queue.on('queueChanged', (tasks) => {
    console.log(`Queue changed, current tasks:`, tasks);
    updateUI(); // 更新 UI
  });

  queue.on('queueStarted', () => console.log('Queue started'));
  queue.on('queuePaused', () => console.log('Queue paused'));
  queue.on('queueResumed', () => console.log('Queue resumed'));
  queue.on('queueClosed', () => console.log('Queue closed'));

  async function updateUI() {
      
    const tasks = await queue.getPendingTasks();
      for (const task of tasks) {
    console.log(`Task ${task.id}: ${task.status} (Retries: ${task.retries || 0})`);}
  }

  const sampleTask = async (params) => {
    console.log('Processing task with params:', params);
    throw new Error('Random task failure');
    console.log('Task completed successfully');
  };

  // 添加任务
  await queue.addTask(sampleTask, { data: 'Task 1' });
*/

